import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Fonction qui génère un rapport PDF pour une proposition d'œuvre à un musée ou une institution
 * @param {Object} artwork - L'objet contenant toutes les données de l'œuvre
 * @param {string} institution - Le nom de l'institution cible
 * @param {string} message - Le message d'intention à inclure dans la proposition
 * @param {Object} artistInfo - Informations supplémentaires sur l'artiste (biographie, etc.)
 * @returns {Promise<Blob>} - Le fichier PDF généré sous forme de Blob
 */
export const generateMuseumProposal = async (artwork, institution, message, artistInfo = {}, meta = {}) => {
  // Création d'un élément div temporaire pour contenir le HTML
  const element = document.createElement('div');
  element.innerHTML = getProposalHTML(artwork, institution, message, artistInfo, meta);
  document.body.appendChild(element);

  try {
    // Conversion du HTML en image avec html2canvas
    const canvas = await html2canvas(element, {
      scale: 2, // Meilleure qualité
      useCORS: true, // Pour charger les images externes
      logging: false,
      onclone: (doc) => {
        // Ajuster le style pour la conversion
        const container = doc.querySelector('.proposal-container');
        if (container) {
          container.style.width = '800px';
          container.style.padding = '20px';
          container.style.margin = '0 auto';
        }
      }
    });

    // Création du PDF
    const imgData = canvas.toDataURL('image/jpeg', 1.0);
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const imgWidth = 210; // A4 width in mm
    const pageHeight = 295; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    // Ajout de la première page
    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    // Ajout de pages supplémentaires si nécessaire
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    // Génération du PDF
    const pdfBlob = pdf.output('blob');
    
    // Nettoyage de l'élément temporaire
    document.body.removeChild(element);
    
    return pdfBlob;
  } catch (error) {
    console.error('Erreur lors de la génération du PDF:', error);
    document.body.removeChild(element);
    throw error;
  }
};

/**
 * Formate la date en format français (JJ/MM/AAAA)
 * @param {Date|string} date - La date à formater
 * @returns {string} - La date formatée
 */
const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('fr-FR');
};

// Libellés FR pour les champs métier de la proposition
const INSTITUTION_TYPE_LABELS = {
  museum: 'Musée',
  foundation: 'Fondation',
  frac: 'FRAC / Fonds régional',
  university: 'Université / École',
  public: 'Collectivité publique',
  other: 'Autre institution',
};
const RESTRICTION_LABELS = {
  none: 'Aucune restriction particulière',
  exhibition_only: 'Exposition uniquement (pas de cession)',
  keep_together: 'Maintien de l’ensemble / collection',
  attribution: 'Mention obligatoire du donateur',
  other: 'Autre condition (voir note d’intention)',
};
const DOCUMENT_LABELS = {
  images: 'Photographies haute définition',
  invoice: 'Facture / preuve d’acquisition',
  provenance: 'Historique de provenance',
  condition: 'Constat d’état',
  authenticity: 'Certificat d’authenticité',
  valuation: 'Estimation / valorisation',
};

// Section dédiée aux objectifs métier (don/legs, prêt, contact institution…)
const formatBusinessSection = (meta = {}) => {
  const rows = [];
  const objectiveLabel =
    meta.proposalType === 'institution_loan'
      ? 'Prêt pour exposition'
      : meta.proposalType === 'institution_gift'
      ? 'Don ou legs à une institution'
      : null;
  if (objectiveLabel) rows.push(['Objectif', objectiveLabel]);
  if (meta.institutionType)
    rows.push(['Type d’institution', INSTITUTION_TYPE_LABELS[meta.institutionType] || meta.institutionType]);
  if (meta.contactName)
    rows.push(['Contact', `${meta.contactName}${meta.contactRole ? ` — ${meta.contactRole}` : ''}`]);
  if (meta.proposalType === 'institution_gift' && meta.restrictions)
    rows.push(['Conditions', RESTRICTION_LABELS[meta.restrictions] || meta.restrictions]);
  rows.push([
    'Propriété confirmée',
    meta.ownershipConfirmed ? 'Oui, le proposant certifie être propriétaire' : 'Non confirmée',
  ]);

  const tableRows = rows.map(([k, v]) => `<tr><td>${k}</td><td>${v}</td></tr>`).join('');

  const documents = Array.isArray(meta.documentsReady) && meta.documentsReady.length
    ? `<h2>Pièces du dossier</h2><ul class="documents-list">${meta.documentsReady
        .map((d) => `<li>${DOCUMENT_LABELS[d] || d}</li>`)
        .join('')}</ul>`
    : '';

  const missionFit = meta.missionFit
    ? `<h2>Adéquation à la mission de l’institution</h2><div class="artwork-description"><p>${meta.missionFit}</p></div>`
    : '';

  const provenanceSummary = meta.provenanceSummary
    ? `<h2>Synthèse de provenance</h2><div class="artwork-description"><p>${meta.provenanceSummary}</p></div>`
    : '';

  if (!rows.length && !documents && !missionFit && !provenanceSummary) return '';

  return `
      <h2>Dossier de proposition</h2>
      <div class="specifications"><table>${tableRows}</table></div>
      ${missionFit}
      ${provenanceSummary}
      ${documents}
  `;
};

/**
 * Génère le contenu HTML pour la proposition à un musée
 * @param {Object} artwork - L'objet contenant toutes les données de l'œuvre
 * @param {string} institution - Le nom de l'institution cible
 * @param {string} message - Le message d'intention à inclure dans la proposition
 * @param {Object} artistInfo - Informations supplémentaires sur l'artiste
 * @returns {string} - Le HTML généré
 */
const getProposalHTML = (artwork, institution, message, artistInfo = {}, meta = {}) => {
  // Sélectionner l'image principale
  const mainImage = Array.isArray(artwork.images) && artwork.images.length > 0
    ? artwork.images.find(img => img.isPrimary) || artwork.images[0]
    : null;
  
  const mainImageUrl = mainImage 
    ? mainImage.url || mainImage.path 
    : 'https://via.placeholder.com/400x400?text=Aucune+image';

  // Récupérer les dimensions
  const dimensions = artwork.specifications?.dimensions || {};
  const framedSize = artwork.specifications?.framedSize || {};
  const paperSize = artwork.specifications?.paperSize || {};
  
  // Formater les dimensions
  const formatDimensions = (dim) => {
    if (!dim || (!dim.height && !dim.width && !dim.depth)) return 'Non spécifiées';
    const unit = dimensions.unit || 'cm';
    
    let result = '';
    if (dim.height) result += `H: ${dim.height} ${unit}`;
    if (dim.width) result += result ? `, L: ${dim.width} ${unit}` : `L: ${dim.width} ${unit}`;
    if (dim.depth) result += result ? `, P: ${dim.depth} ${unit}` : `P: ${dim.depth} ${unit}`;
    
    return result || 'Non spécifiées';
  };

  // Formater la provenance
  const formatProvenance = () => {
    if (!artwork.provenance || !Array.isArray(artwork.provenance) || artwork.provenance.length === 0) {
      return '<p>Aucune information de provenance disponible</p>';
    }

    return artwork.provenance
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .map(prov => `
        <div class="provenance-item">
          <div class="provenance-date">${formatDate(prov.date)}</div>
          <div>${prov.description}</div>
          ${prov.owner ? `<div>Propriétaire: ${prov.owner}</div>` : ''}
          ${prov.location ? `<div>Lieu: ${prov.location}</div>` : ''}
          ${prov.acquisitionMethod ? `<div>Mode d'acquisition: ${prov.acquisitionMethod}</div>` : ''}
        </div>
      `)
      .join('');
  };

  // Formater les documents
  const formatDocuments = () => {
    if (!artwork.documents || !Array.isArray(artwork.documents) || artwork.documents.length === 0) {
      return '<li>Aucun document disponible</li>';
    }

    return artwork.documents
      .map(doc => `<li>${doc.title || doc.name || 'Document sans titre'} (${doc.type || 'Type non spécifié'})</li>`)
      .join('');
  };

  // Formater les expositions passées (si disponibles)
  const formatExhibitions = () => {
    if (!artistInfo.exhibitions || !Array.isArray(artistInfo.exhibitions) || artistInfo.exhibitions.length === 0) {
      return '<p>Aucune information sur les expositions précédentes</p>';
    }

    return artistInfo.exhibitions
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .map(expo => `
        <div class="exhibition-item">
          <div class="exhibition-title">${expo.title || 'Exposition sans titre'}</div>
          <div>${expo.venue || ''}, ${expo.location || ''}</div>
          <div>${formatDate(expo.startDate)}${expo.endDate ? ` - ${formatDate(expo.endDate)}` : ''}</div>
          ${expo.description ? `<div class="mt-1">${expo.description}</div>` : ''}
        </div>
      `)
      .join('');
  };

  // Information d'édition
  const editionInfo = artwork.isEdition 
    ? (artwork.editionInfo || 'Œuvre en édition limitée (détails non spécifiés)') 
    : 'Œuvre unique';

  // État de conservation
  const condition = artwork.condition || 'Non spécifié';
const conditionNotes = artwork.conditionNotes || "Aucune note spécifique sur l'état de conservation";

  // Biographie de l'artiste
  const biography = artistInfo.biography || `${artwork.artist || 'L\'artiste'} est un créateur contemporain dont l'œuvre explore diverses thématiques à travers ${artwork.medium || 'différents médiums'}.`;

  // Pertinence artistique
  const artisticRelevance = artistInfo.relevance || 'Cette œuvre s\'inscrit dans une démarche artistique contemporaine significative, abordant des thématiques pertinentes pour notre époque.';

  // Description de l'œuvre
  const artworkDescription = artistInfo.description || `${artwork.title || 'Cette œuvre'} ${artwork.year ? `créée en ${artwork.year}` : ''} illustre la maîtrise technique et la vision artistique de ${artwork.artist || 'l\'artiste'}.`;

  // HTML du rapport
  return `
    <div class="proposal-container">
      <div class="header">
        <h1>${
          meta.proposalType === 'institution_gift'
            ? "Proposition de don ou legs d'œuvre"
            : "Proposition d'œuvre pour exposition"
        }</h1>
        <div class="institution">${institution || 'Institution destinataire'}</div>
        <p>Date de proposition: ${formatDate(new Date())}</p>
      </div>

      <div class="artwork-details">
        <div class="artwork-image">
          <img src="${mainImageUrl}" alt="${artwork.title || 'Œuvre sans titre'}">
        </div>
        <div class="artwork-info">
          <div class="info-group">
            <div class="info-label">Titre</div>
            <div class="info-value">${artwork.title || 'Sans titre'}</div>
          </div>
          <div class="info-group">
            <div class="info-label">Artiste</div>
            <div class="info-value">${artwork.artist || 'Artiste inconnu'}</div>
          </div>
          <div class="info-group">
            <div class="info-label">Année de création</div>
            <div class="info-value">${artwork.year || 'Non spécifiée'}</div>
          </div>
          <div class="info-group">
            <div class="info-label">Type d'œuvre</div>
            <div class="info-value">${artwork.typeOfArt || 'Non spécifié'}</div>
          </div>
          <div class="info-group">
            <div class="info-label">Technique/Medium</div>
            <div class="info-value">${artwork.medium || 'Non spécifié'}</div>
          </div>
        </div>
      </div>

      ${formatBusinessSection(meta)}

      <div class="artist-biography">
        <h3>Biographie de l'artiste</h3>
        <p>${biography}</p>
      </div>

      <div class="artistic-relevance">
        <h3>Pertinence artistique et culturelle</h3>
        <p>${artisticRelevance}</p>
      </div>

      <h2>Description de l'œuvre</h2>
      <div class="artwork-description">
        <p>${artworkDescription}</p>
      </div>

      <h2>Spécifications</h2>
      <div class="specifications">
        <table>
          <tr>
            <td>Dimensions (sans cadre)</td>
            <td>${formatDimensions(dimensions)}</td>
          </tr>
          <tr>
            <td>Dimensions (avec cadre)</td>
            <td>${framedSize ? formatDimensions(framedSize) : 'Non spécifiées'}</td>
          </tr>
          <tr>
            <td>Dimensions du papier</td>
            <td>${paperSize ? formatDimensions(paperSize) : 'Non spécifiées'}</td>
          </tr>
          <tr>
            <td>Édition</td>
            <td>${editionInfo}</td>
          </tr>
          <tr>
            <td>Sujet</td>
            <td>${artwork.subjectMatter || 'Non spécifié'}</td>
          </tr>
        </table>
      </div>

      <h2>Expositions précédentes</h2>
      <div class="exhibitions-list">
        ${formatExhibitions()}
      </div>

      <h2>Provenance</h2>
      <div class="provenance">
        ${formatProvenance()}
      </div>

      <h2>État de conservation</h2>
      <div class="condition">
        <p>${condition}</p>
        <div class="condition-notes">
          <p>${conditionNotes}</p>
        </div>
      </div>

      <h2>Documents d'authentification</h2>
      <ul class="documents-list">
        ${formatDocuments()}
      </ul>

      <h2>Note d'intention</h2>
      <div class="intention-note">
        <p>${message || 'Aucune note d\'intention fournie'}</p>
      </div>

      <div class="conclusion">
        <p>Cette œuvre représente une opportunité significative d'enrichir votre programmation expositionnelle par sa pertinence artistique, historique et culturelle. Nous serions honorés de contribuer à votre mission de médiation culturelle en vous proposant cette pièce exceptionnelle.</p>
      </div>

      <div class="footer">
        <p>Document généré le ${formatDate(new Date())} | Confidentiel</p>
      </div>
    </div>
  `;
};

// Fonction pour télécharger le PDF
export const downloadMuseumProposal = async (artwork, institution, message, artistInfo = {}, filename = 'proposition-musee.pdf') => {
  try {
    let meta = {};
    // Nouvelle signature : un seul objet payload { artwork, institution, message, proposalType, additionalData }
    if (artwork && typeof artwork === 'object' && ('additionalData' in artwork || 'proposalType' in artwork)) {
      const payload = artwork;
      const add = payload.additionalData || {};
      artwork = payload.artwork || {};
      institution = payload.institution || '';
      message = payload.message || '';
      artistInfo = {
        biography: add.artistBio,
        relevance: add.artisticRelevance,
        description: add.artworkDescription,
        exhibitions: add.exhibitions,
      };
      meta = {
        proposalType: payload.proposalType,
        institutionType: add.institutionType,
        contactName: add.contactName,
        contactRole: add.contactRole,
        missionFit: add.missionFit,
        provenanceSummary: add.provenanceSummary,
        ownershipConfirmed: add.ownershipConfirmed,
        restrictions: add.restrictions,
        documentsReady: add.documentsReady,
      };
    }
    const pdfBlob = await generateMuseumProposal(artwork, institution, message, artistInfo, meta);
    
    // Création d'un lien de téléchargement
    const link = document.createElement('a');
    link.href = URL.createObjectURL(pdfBlob);
    link.download = filename;
    
    // Cliquer sur le lien pour télécharger
    document.body.appendChild(link);
    link.click();
    
    // Nettoyer
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    
    return true;
  } catch (error) {
    console.error('Erreur lors du téléchargement du PDF:', error);
    return false;
  }
};